import { useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Col, Collapse, List, message, Modal, Select, Spin, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { CompanyFeatureSyncInput } from '../../models/company-feature-sync-input-interface';
import { CompanyProductVersionModel, Feature, ProductVersionModel } from '../../models/product-interface';
import { ProductQueries } from '../../queries/product-queries';
import CloseableErrorMessage from '../CloseableErrorMessage';

const { Panel } = Collapse;

interface SyncProductModalProps {
  isVisible: boolean;
  handleClose(): void;
  product: CompanyProductVersionModel | undefined;
  companyIds: number[];
  companyNames: string[];
  canUpdate: boolean;
}

export default function SyncProductModal({
  isVisible,
  handleClose,
  product,
  companyIds,
  companyNames,
  canUpdate
}: SyncProductModalProps) {
  const maxProductVersionId = Math.max(...product?.newerVersions?.map((v) => v.versionId) ?? []);
  const latestProduct = product?.newerVersions?.find(v => v.versionId === maxProductVersionId);
  const [selectedSyncVersion, setSelectedSyncVersion] = useState<ProductVersionModel | undefined>();
  const syncFeatursWithVersionMutation = ProductQueries.useSyncFeaturesWithVersion(product?.product?.id);
  const queryClient = useQueryClient();

  useEffect(() => {
    setSelectedSyncVersion(latestProduct);
  }, [latestProduct])

  if (!product?.newerVersions?.length || !companyIds?.length) return null;

  const newFeatures = (version: ProductVersionModel | undefined) => {
    if (!product?.newerVersions?.length || !version) return [];

    const existingFeatures = product.features.map((f) => f.id);

    const newFeatures = version?.features.filter(
      (f) => !existingFeatures.includes(f.id)
    );
    return newFeatures;
  };

  const oldFeatures = (version: ProductVersionModel | undefined) => {
    if (!product.newerVersions?.length || !version) return [];

    const oldFeatures = product.features.filter(
      (feature) => !version?.features?.map((f) => f.id).includes(feature.id)
    );
    return oldFeatures;
  };

  const recordLimitChanges = (version: ProductVersionModel | undefined) => {
    if (!product.newerVersions?.length || !version) return [];

    const recordLimitChanges = version?.features?.filter(
      (feature) =>
        feature?.allowLimit &&
        !!product?.features?.find((f) => f.id === feature.id) &&
        product?.features?.find((f) => f.id === feature.id)?.recordLimit !==
          feature.recordLimit
    );
    return recordLimitChanges;
  };

  const handleSync = async () => {
    if (!!selectedSyncVersion) {
      let errorCompanyIds: number[] = [];

      await Promise.all(companyIds.map(async (company) => {
        const data: CompanyFeatureSyncInput = {
          companyId: company,
          productId: product.product.salesforce_id.toString(),
          targetVersion: selectedSyncVersion.versionId,
          targetCurrentVersion: false,
          updateLocalCache: true,
        };

        await syncFeatursWithVersionMutation.mutateAsync(data, {
          onError: () => (errorCompanyIds = [...errorCompanyIds, company]),
        }); 
      }));

      if (!!errorCompanyIds?.length) {
        if (companyIds.length === 1) {
          CloseableErrorMessage('Error updating product version.');
        } else {
          CloseableErrorMessage(
            `Error updating product version for ${
              errorCompanyIds.length === 1 ? 'company ' : 'the following companies: '
            }${errorCompanyIds.join(', ')}`
          );
        }
      } else {
        queryClient.resetQueries({ queryKey: ["companyProducts"], type: 'active'});
        setSelectedSyncVersion(undefined);
        handleClose();
        message.success('Updated product version successfully.');
      }
    }
  };

  return (
    <Modal
      bodyStyle={{ overflowY: 'scroll', maxHeight: 'calc(100vh - 200px)' }}
      centered
      title={`Sync ${product.product.name} Features`}
      visible={isVisible}
      onCancel={() => {
        setSelectedSyncVersion(undefined);
        handleClose();
      }}
      footer={canUpdate ? [
        <Button
          id='syncProductsButton'
          type='primary'
          key='syncProducts'
          onClick={async () => await handleSync()}
          loading={syncFeatursWithVersionMutation.isLoading}
          disabled={!selectedSyncVersion}
        >
          Sync
        </Button>,
      ] : []}
    >
      <div style={{ paddingBottom: 10 }}>
        <b>{companyNames?.length > 1 ? 'Companies: ' : 'Company: '}</b>
        {companyNames.join(', ')}
      </div>
      {syncFeatursWithVersionMutation.isLoading ? (
        <Spin
          spinning={true}
          style={{
            height: 100,
            width: '100%',
            alignContent: 'center',
            justifyContent: 'center',
          }}
        />
      ) : (
        <>
          <Alert
            type='info'
            message={
              <>
                {`This will sync the features for the ${
                  companyNames?.length > 1 ? 'companies ' : 'company '
                } listed above with the feature set of a newer version of this product. Below is a preview of what features will be added or removed.`}
              </>
            }
          />

          <br />
          <b>Choose Sync Version:</b>
          <br />
          <Select
            style={{ width: 250 }}
            onChange={(version: number) =>
              setSelectedSyncVersion(
                product?.newerVersions.find((v) => v.versionId === version)
              )
            }
            defaultValue={
              product?.newerVersions?.sort((a, b) =>
                a.versionId > b.versionId ? -1 : 1
              )[0].versionId
            }
          >
            {product?.newerVersions
              ?.sort((a, b) => (a.versionId > b.versionId ? -1 : 1))
              .map((v) => (
                <Select.Option key={v.versionId} value={v.versionId}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>
                      v{v.versionId} -{' '}
                      {new Date(v.date).toLocaleDateString('en-us')}
                      <b>
                        {v.versionId === maxProductVersionId ? ' - Latest' : ''}
                      </b>
                    </span>
                    <span>
                      {!!newFeatures(v)?.length ? (
                        <Tooltip
                          overlay={`${newFeatures(v).length} New Feature(s)`}
                        >
                          <Badge color='green' />
                        </Tooltip>
                      ) : null}
                      {!!oldFeatures(v)?.length ? (
                        <Tooltip
                          overlay={`${
                            oldFeatures(v).length
                          } Feature(s) Removed`}
                        >
                          <Badge color='red' />
                        </Tooltip>
                      ) : null}
                      {!!recordLimitChanges(v)?.length ? (
                        <Tooltip
                          overlay={`${
                            recordLimitChanges(v).length
                          } Record Limit Change(s)`}
                        >
                          <Badge color='orange' />
                        </Tooltip>
                      ) : null}
                    </span>
                  </div>
                </Select.Option>
              ))}
          </Select>
          <br />
          <br />
          <Collapse defaultActiveKey={['new', 'removed', 'recordLimitChange']}>
            {!!newFeatures(selectedSyncVersion)?.length && (
              <Panel
                key='new'
                header={<b style={{ color: 'green' }}>New Features</b>}
                style={{ borderWidth: 0, backgroundColor: 'transparent' }}
                showArrow={true}
              >
                <FeatureList features={newFeatures(selectedSyncVersion)} />
              </Panel>
            )}
            {!!oldFeatures(selectedSyncVersion)?.length && (
              <Panel
                key='removed'
                header={
                  <b style={{ color: 'red' ?? '#000000' }}>Removed Features</b>
                }
                style={{ borderWidth: 0, backgroundColor: 'transparent' }}
                showArrow={true}
              >
                <FeatureList features={oldFeatures(selectedSyncVersion)} />
              </Panel>
            )}
            {!!recordLimitChanges(selectedSyncVersion)?.length && (
              <Panel
                key='recordLimitChange'
                header={
                  <b style={{ color: 'orange' ?? '#000000' }}>
                    Record Limit Changes
                  </b>
                }
                style={{ borderWidth: 0, backgroundColor: 'transparent' }}
                showArrow={true}
              >
                <FeatureList
                  features={recordLimitChanges(selectedSyncVersion)}
                />
              </Panel>
            )}
            {!newFeatures(selectedSyncVersion)?.length &&
              !oldFeatures(selectedSyncVersion)?.length &&
              !recordLimitChanges(selectedSyncVersion)?.length && (
                <i>No feature changes with this version.</i>
              )}
            <Panel
              key='All Features'
              header={<b style={{ color: '#000000' }}>All Features</b>}
              style={{ borderWidth: 0, backgroundColor: 'transparent' }}
              showArrow={true}
            >
              <FeatureList features={selectedSyncVersion?.features ?? []} />
            </Panel>
          </Collapse>
        </>
      )}
    </Modal>
  );
}

const FeatureList = ({
  features,
}: {
  features: Feature[];
}) => {
  return (
    <Col>
      <List
        dataSource={features?.sort((a, b) => a.name.localeCompare(b.name))}
        renderItem={(item) => (
          <List.Item style={{ borderWidth: 0, padding: 5 }}>
            <div style={{ display: 'inline-flex' }}>
              {item.name}
              {item.allowLimit && (
                <>
                  &nbsp;
                  <Badge
                    count={
                      item.recordLimit > 0
                        ? `Limit ${item.recordLimit}`
                        : 'Unlimited'
                    }
                  />
                </>
              )}
            </div>
          </List.Item>
        )}
      />
    </Col>
  );
};